import { useEffect, useState } from 'react';

/**
 * Reactive viewport-width gate. Returns `true` when the current viewport
 * width is at least `minPx` and updates live when the user resizes the
 * window or rotates the device.
 *
 * SSR-safe: defaults to `false` when `window` or `matchMedia` is undefined.
 *
 * Mirrors the shape of `usePrefersReducedMotion` (same directory) — the
 * tour layout dispatcher uses this to fall back from the 640px-wide split
 * card to the 400px compact card when the viewport is below 1280px.
 */
export const useMinViewport = (minPx: number): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(`(min-width: ${minPx}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(`(min-width: ${minPx}px)`);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [minPx]);

  return matches;
};
